import { FUNDING } from "@/data/site";
import { useT } from "@/i18n/I18nProvider";

export const Field = ({ label, name, value, onChange, type = "text", required, placeholder, textarea, span }) => (
  <label className={`block ${span ? "md:col-span-2" : ""}`}>
    <span className="label">{label}{required && <span className="text-[var(--coral)]"> *</span>}</span>
    {textarea ? (
      <textarea data-testid={`ref-${name}`} name={name} value={value} onChange={onChange} required={required} placeholder={placeholder} className="field min-h-[96px] resize-y" />
    ) : (
      <input data-testid={`ref-${name}`} name={name} type={type} value={value} onChange={onChange} required={required} placeholder={placeholder} className="field" />
    )}
  </label>
);

const pill = (on) => `inline-flex items-center gap-2 text-sm cursor-pointer px-4 py-2 rounded-md border-2 transition-colors ${on ? "bg-[var(--blue)] text-white border-[var(--blue)]" : "bg-white border-slate-200 hover:border-[var(--blue)]"}`;

// Option values stay in English (stored in the database); only the visible label is translated.
export const Radio = ({ label, name, value, onChange, options }) => {
  const t = useT();
  return (
    <div>
      <span className="label">{label}</span>
      <div className="flex flex-wrap gap-2" data-testid={`ref-${name}`}>
        {options.map((o) => (
          <label key={o} className={pill(value === o)}>
            <input type="radio" name={name} value={o} checked={value === o} onChange={onChange} className="sr-only" data-testid={`ref-${name}-${o.toLowerCase().replace(/\s+/g, "-")}`} />
            {t(o)}
          </label>
        ))}
      </div>
    </div>
  );
};

export const FundingType = ({ selected, onToggle }) => {
  const t = useT();
  return (
    <div className="md:col-span-2">
      <span className="label">{t("Funding type")}</span>
      <div className="flex flex-wrap gap-2" data-testid="ref-funding-type">
        {FUNDING.map((o) => (
          <label key={o} className={pill(selected.includes(o))}>
            <input type="checkbox" checked={selected.includes(o)} onChange={() => onToggle(o)} className="sr-only" data-testid={`ref-funding-${o.toLowerCase().replace(/\s+/g, "-")}`} />
            {t(o)}
          </label>
        ))}
      </div>
    </div>
  );
};

export const FormSection = ({ n, title, children }) => (
  <fieldset className="card p-7 sm:p-10" id={`section-${n}`}>
    <legend className="sr-only">{title}</legend>
    <div className="flex items-center gap-4 mb-7">
      <span className="w-9 h-9 rounded-md bg-[var(--coral)] text-white font-bold text-sm flex items-center justify-center">{n}</span>
      <h2 className="text-xl sm:text-2xl font-semibold">{title}</h2>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
  </fieldset>
);

export const ParticipantSection = ({ f, onChange }) => {
  const t = useT();
  return (
    <FormSection n={1} title={t("Details of the person requiring support")}>
      <Field label={t("Name")} name="participant_name" value={f.participant_name} onChange={onChange} required />
      <Field label={t("Date of birth")} name="participant_dob" type="date" value={f.participant_dob} onChange={onChange} />
      <Field label={t("Address")} name="participant_address" value={f.participant_address} onChange={onChange} />
      <Field label={t("Email")} name="participant_email" type="email" value={f.participant_email} onChange={onChange} />
      <Field label={t("Phone")} name="participant_phone" type="tel" value={f.participant_phone} onChange={onChange} required />
      <Field label={t("Gender")} name="participant_gender" value={f.participant_gender} onChange={onChange} />
      <Radio label={t("Aboriginal or Torres Strait Islander descent?")} name="atsi_descent" value={f.atsi_descent} onChange={onChange} options={["Yes", "No"]} />
      <Field label={t("Cultural background and preferred language")} name="cultural_background" value={f.cultural_background} onChange={onChange} />
    </FormSection>
  );
};

export const NdisSection = ({ f, onChange, onToggleFunding }) => {
  const t = useT();
  return (
    <FormSection n={2} title={t("NDIS information")}>
      <Field label={t("NDIS number")} name="ndis_number" value={f.ndis_number} onChange={onChange} />
      <Field label={t("NDIS plan dates")} name="ndis_plan_dates" value={f.ndis_plan_dates} onChange={onChange} placeholder={t("e.g. 01/07/2025 – 30/06/2026")} />
      <FundingType selected={f.funding_type} onToggle={onToggleFunding} />
      <Field label={t("Plan manager")} name="plan_manager" value={f.plan_manager} onChange={onChange} />
      <Field label={t("Plan manager email")} name="plan_manager_email" type="email" value={f.plan_manager_email} onChange={onChange} />
      <Field label={t("Plan manager contact number")} name="plan_manager_phone" type="tel" value={f.plan_manager_phone} onChange={onChange} />
      <Radio label={t("Copy of NDIS plan provided")} name="plan_copy_provided" value={f.plan_copy_provided} onChange={onChange} options={["Yes", "No"]} />
      <Radio label={t("Registered with another NDIS provider?")} name="other_provider" value={f.other_provider} onChange={onChange} options={["Yes", "No"]} />
      <Field label={t("NDIS provider name")} name="other_provider_name" value={f.other_provider_name} onChange={onChange} />
      <Field label={t("Service type (schedule of support)")} name="service_type" value={f.service_type} onChange={onChange} textarea span />
    </FormSection>
  );
};

export const DisabilitySection = ({ f, onChange }) => {
  const t = useT();
  return (
    <FormSection n={3} title={t("Disability and reason for referral")}>
      <Field label={t("Primary")} name="disability_primary" value={f.disability_primary} onChange={onChange} />
      <Field label={t("Secondary (if known)")} name="disability_secondary" value={f.disability_secondary} onChange={onChange} />
      <Field label={t("Physical mobility")} name="physical_mobility" value={f.physical_mobility} onChange={onChange} />
      <Field label={t("Cognitive ability")} name="cognitive_ability" value={f.cognitive_ability} onChange={onChange} />
      <Field label={t("Other relevant information we should be aware of")} name="other_info" value={f.other_info} onChange={onChange} textarea span />
    </FormSection>
  );
};

export const CarerSection = ({ f, onChange }) => {
  const t = useT();
  return (
    <FormSection n={4} title={t("Primary carer / next of kin / guardian (if required)")}>
      <Field label={t("Name")} name="carer_name" value={f.carer_name} onChange={onChange} />
      <Field label={t("Relationship")} name="carer_relationship" value={f.carer_relationship} onChange={onChange} />
      <Field label={t("Address")} name="carer_address" value={f.carer_address} onChange={onChange} />
      <Field label={t("Phone")} name="carer_phone" type="tel" value={f.carer_phone} onChange={onChange} />
      <Field label={t("Email")} name="carer_email" type="email" value={f.carer_email} onChange={onChange} />
    </FormSection>
  );
};

export const ReferrerSection = ({ f, onChange }) => {
  const t = useT();
  return (
    <FormSection n={5} title={t("Referrer details")}>
      <Field label={t("Name")} name="referrer_name" value={f.referrer_name} onChange={onChange} required />
      <Field label={t("Relationship")} name="referrer_relationship" value={f.referrer_relationship} onChange={onChange} />
      <Field label={t("Organisation")} name="referrer_organisation" value={f.referrer_organisation} onChange={onChange} />
      <Field label={t("Address")} name="referrer_address" value={f.referrer_address} onChange={onChange} />
      <Field label={t("Phone")} name="referrer_phone" type="tel" value={f.referrer_phone} onChange={onChange} />
      <Field label={t("Email")} name="referrer_email" type="email" value={f.referrer_email} onChange={onChange} required />
    </FormSection>
  );
};
